<template>
  <div v-if="authIsReady" class="p-4 sm:p-6 lg:p-8">
    <div class="max-w-7xl mx-auto">
      <header class="mb-8 text-center">
        <router-link to="/" class="inline-block">
          <img class="h-16 w-auto" src="@/assets/logo.png" alt="Castive Logo">
        </router-link>
      </header>
      <main class="max-w-7xl mx-auto">
        <div class="bg-white rounded-xl shadow-lg overflow-hidden p-8">
          <h1 class="text-2xl font-bold text-gray-800 border-b pb-4">Guest Release Form Details</h1>
          <div v-if="loading" class="text-center py-10"><p>Loading submission...</p></div>
          <div v-else-if="error" class="text-center py-10 text-red-600"><p>{{ error }}</p></div>
          <div v-else-if="submission && client" class="mt-6 space-y-8">
            <div>
              <h2 class="text-lg font-semibold text-gray-700">Agreement Details</h2>
              <div class="mt-2 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                <div><p class="text-sm font-medium text-gray-500">Guest Name</p><p class="text-lg text-gray-900">{{ submission.guestName }}</p></div>
                <div><p class="text-sm font-medium text-gray-500">Guest Email</p><p class="text-lg text-gray-900">{{ submission.guestEmail }}</p></div>
                <div><p class="text-sm font-medium text-gray-500">Submitted At</p><p class="text-lg text-gray-900">{{ formatDate(submission.submittedAt) }}</p></div>
                <div><p class="text-sm font-medium text-gray-500">Status</p><p class="text-lg font-bold capitalize" :class="submission.status === 'completed' ? 'text-green-600' : 'text-yellow-600'">{{ submission.status.replace(/_/g, ' ') }}</p></div>
              </div>
            </div>
            
            <div class="border-t pt-8">
              <h2 class="text-lg font-semibold text-gray-700">Recording Details</h2>
              <div class="mt-2 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                <div v-if="submission.podcastSubject"><p class="text-sm font-medium text-gray-500">Podcast Subject / Title</p><p class="text-lg text-gray-900">{{ submission.podcastSubject }}</p></div>
                <div><p class="text-sm font-medium text-gray-500">Recording Date & Time</p><p class="text-lg text-gray-900">{{ formatFullDate(submission.recordingDate) }}</p></div>
                <div class="md:col-span-2" v-if="submission.recordingLink">
                  <p class="text-sm font-medium text-gray-500">Recording Link</p>
                  <a :href="submission.recordingLink" target="_blank" class="text-lg text-brand-purple-dark hover:text-brand-pink-static break-all">{{ submission.recordingLink }}</a>
                </div>
              </div>
            </div>

            <div class="border-t pt-8">
              <h2 class="text-lg font-semibold text-gray-700">Podcast Guest Release Agreement</h2>
              <div class="prose max-w-none text-gray-700 mt-2 p-4 bg-gray-50 rounded-md border h-64 overflow-y-auto" v-html="agreementTextHtml"></div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-8 items-start border-t pt-8">
              <div>
                <h2 class="text-lg font-semibold text-gray-700">Guest Signature</h2>
                <div class="mt-2 border rounded-md p-2 bg-gray-50 h-[200px] flex items-center justify-center">
                  <img :src="submission.guestSignature" alt="Guest Signature" class="max-w-full max-h-full">
                </div>
              </div>
              <div>
                <div class="flex justify-between items-center"><h2 class="text-lg font-semibold text-gray-700">Host Signature</h2><button v-if="submission.status === 'pending_host_signature'" @click="clearHostSignature" class="text-sm text-brand-purple-dark hover:text-brand-pink-static">Clear</button></div>
                <div v-if="submission.hostSignature" class="mt-2 border rounded-md p-2 bg-gray-50 h-[200px] flex items-center justify-center"><img :src="submission.hostSignature" alt="Host Signature" class="max-w-full max-h-full"></div>
                <div v-if="submission.status === 'pending_host_signature'" class="mt-2 border border-gray-300 rounded-md">
                  <Vue3Signature ref="hostSignaturePad" :sigOption="signatureOptions" :h="'200px'" class="w-full"></Vue3Signature>
                </div>
                <p v-if="signatureError" class="mt-2 text-sm text-red-600">Please provide a signature.</p>
                <div v-if="submission.status === 'pending_host_signature'" class="mt-4"><button @click="completeAgreement" :disabled="isCompleting" class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-brand-purple-dark hover:opacity-90 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-orange"><span v-if="isCompleting">Completing...</span><span v-else>Complete Agreement</span></button></div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <footer class="mt-12 text-center py-4"><div class="max-w-7xl mx-auto flex items-center justify-center space-x-2"><img src="@/assets/logo.png" alt="Castive Logo" class="h-6 w-auto opacity-75"><p class="text-sm text-gray-600 font-medium">Powered by Castive</p></div></footer>
    </div>
  </div>
</template>

<script>
import { db } from '@/firebase';
import { doc, getDoc, updateDoc, serverTimestamp, collection, query, where, getDocs } from 'firebase/firestore';
import Vue3Signature from 'vue3-signature';
import { mapState } from 'vuex';

export default {
  name: 'SubmissionDetailView',
  components: { Vue3Signature },
  data() {
    return {
      submission: null,
      client: null,
      loading: true,
      error: null,
      signatureOptions: { penColor: "#000000", backgroundColor: "#ffffff" },
      signatureError: false,
      isCompleting: false,
    }
  },
  computed: {
    ...mapState(['authIsReady']),
    agreementTextHtml() {
      if (!this.client) return '';
      return `
        <p>This Podcast Guest Release Agreement (the "Agreement") is made and entered into by and between <strong>${this.submission.guestName}</strong> ("Guest") and <strong>The ${this.client.podcastName} Podcast</strong> ("Producer") for the podcast tentatively titled "<strong>${this.client.podcastName}</strong>" (the "Podcast").</p>
        <h3>1. Consent and Grant of Rights</h3>
        <p>The Guest hereby irrevocably consents to the recording, use, and reuse by Producer...[etc]</p>
      `;
    },
  },
  methods: {
    formatDate(timestamp) {
      if (!timestamp?.toDate) return 'N/A';
      return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(timestamp.toDate());
    },
    formatFullDate(timestamp) {
        if (!timestamp?.toDate) return 'Not yet scheduled.';
        return new Intl.DateTimeFormat('en-GB', { dateStyle: 'full', timeStyle: 'short' }).format(timestamp.toDate());
    },
    clearHostSignature() { this.$refs.hostSignaturePad.clear(); this.signatureError = false; },
    async completeAgreement() {
      if (this.$refs.hostSignaturePad.isEmpty()) { this.signatureError = true; return; }
      this.isCompleting = true;
      try {
        const hostSignatureDataURL = this.$refs.hostSignaturePad.save('image/png');
        const submissionRef = doc(db, 'submissions', this.submission.id);
        await updateDoc(submissionRef, { status: 'completed', hostSignature: hostSignatureDataURL, hostSignedAt: serverTimestamp() });
        this.$router.push('/');
      } catch (error) {
        console.error("Error updating document:", error);
        alert("There was an error completing the agreement.");
      } finally {
        this.isCompleting = false;
      }
    }
  },
  async created() {
    try {
      const docRef = doc(db, 'submissions', this.$route.params.id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        this.submission = { id: docSnap.id, ...docSnap.data() };
        if (this.submission.clientId) {
          const clientsQuery = query(collection(db, 'clients'), where('__name__', '==', this.submission.clientId));
          const clientSnapshot = await getDocs(clientsQuery);
          if (!clientSnapshot.empty) {
            this.client = clientSnapshot.docs[0].data();
          } else {
             throw new Error("Associated client data could not be found.");
          }
        } else {
          throw new Error("Submission is missing client information.");
        }
      } else {
        this.error = "Submission not found.";
      }
    } catch (err) {
      console.error("Error fetching document:", err);
      this.error = "Failed to load submission data.";
    } finally {
      this.loading = false;
    }
  },
}
</script>

<style scoped>
.prose :deep(p) { margin-bottom: 1em; }
.prose :deep(h3) { font-weight: bold; margin-top: 1.5em; margin-bottom: 0.5em; }
.prose :deep(strong) { font-weight: bold; }
</style>